/**
 * Client-side image preparation for the contact form.
 *
 * Datastar transmits bound files as base64 inside the request body, which has a
 * practical ~1 MiB-per-request ceiling. Phone photos are far larger, so we
 * downscale + re-encode each image to JPEG here BEFORE it becomes a signal.
 *
 * Exposed as `window.wwPrepareImages(fileList)` so a Datastar `data-on:change`
 * expression can `await` it and assign the result to a signal:
 *
 *   data-on:change="$contact.files = await window.wwPrepareImages(el.files)"
 *
 * Returns an array shaped like Datastar's own file binding:
 *   { name: string, contents: string /* raw base64 *\/, mime: string }[]
 */

const MAX_DIMENSION = 1600; // px, longest edge
const TARGET_BYTES = 700 * 1024; // aim for < ~700 KB decoded per image
const MIN_QUALITY = 0.4;
const MAX_FILES = 6;

export interface PreparedFile {
  name: string;
  contents: string;
  mime: string;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

async function resizeOne(file: File): Promise<PreparedFile | null> {
  if (!file.type.startsWith('image/')) return null;

  let img: HTMLImageElement;
  try {
    img = await loadImage(file);
  } catch {
    return null;
  }

  let width = img.naturalWidth || img.width;
  let height = img.naturalHeight || img.height;
  if (!width || !height) return null;

  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const scale = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, width, height);

  // Step quality down until the decoded size is under target (or we hit the floor).
  let quality = 0.85;
  let dataUrl = canvas.toDataURL('image/jpeg', quality);
  while (dataUrl.length * 0.75 > TARGET_BYTES && quality > MIN_QUALITY) {
    quality = Math.round((quality - 0.1) * 100) / 100;
    dataUrl = canvas.toDataURL('image/jpeg', quality);
  }

  const contents = dataUrl.split(',')[1] ?? '';
  if (!contents) return null;

  const base = file.name.replace(/\.[^.]+$/, '') || 'photo';
  return { name: `${base}.jpg`, contents, mime: 'image/jpeg' };
}

export async function prepareImages(
  fileList: FileList | File[] | null,
): Promise<PreparedFile[]> {
  if (!fileList) return [];
  const files = Array.from(fileList).slice(0, MAX_FILES);
  const prepared: PreparedFile[] = [];
  for (const file of files) {
    const result = await resizeOne(file);
    if (result) prepared.push(result);
  }
  return prepared;
}

declare global {
  interface Window {
    wwPrepareImages: typeof prepareImages;
  }
}

window.wwPrepareImages = prepareImages;
